// מסך סקירה ואישור של חילוץ שפה גרפית: לוגואים ליד המקור, פלטה, פונט, חומרים, סתירות.
import { useState } from 'react';
import { api } from '../../api';
import { HEX_RE, useAction } from './common';
import { COLOR_ROLES } from './BrandKitEditor';

export interface BSource { id: string; kind: 'url' | 'pdf' | 'image'; url: string | null; asset_id: string | null; preferred: boolean; status: string; error: string | null; fetched_at: string | null; checksum?: string | null; name?: string; assetUrl?: string | null; pages?: string[] }
export interface Candidate {
  id: string; kind: string; value: any; sourceId?: string; source_id?: string; location: any; confidence: number; provenance: 'extracted' | 'inferred';
  status: 'pending' | 'approved' | 'rejected'; asset_id: string | null; assetUrl?: string | null;
}
export interface Extraction {
  extraction: { id: string; client_id: string; target_brand_id: string | null; campaign_id: string | null; new_brand_name: string | null; status: string; result?: any };
  sources: BSource[]; candidates: Candidate[]; conflicts: any[]; warnings?: unknown[]; sourceErrors?: { sourceId: string; error: string }[];
}
type Crop = { left: number; top: number; width: number; height: number };
type ValuePatch = { hex?: string; role?: string; name?: string; variant?: string; family?: string; weights?: number[]; text?: string; crop?: Crop; removeBg?: boolean };

const KIND_L: Record<string, string> = { logo: 'לוגו', color: 'צבע', font: 'פונט', spacing: 'מרווחים', image_style: 'סגנון תמונות', graphic: 'אלמנט גרפי', tone: 'טון מסרים', composition: 'קומפוזיציה', material: 'חומר' };
const LOGO_VARIANT_L: Record<string, string> = { primary: 'ראשי', light: 'בהיר', dark: 'כהה', icon: 'אייקון', other: 'אחר' };
const srcOf = (c: Candidate) => c.sourceId ?? c.source_id ?? '';

export function isPhotoColor(c: Candidate) {
  const v = c.value ?? {}; const l = c.location ?? {};
  return !!(v.isPhotoDominant || v.fromPhoto || v.origin === 'photo' || l.kind === 'photo' || (Array.isArray(v.usage) && v.usage.includes('photo-dominant')));
}
export function isUncertainFont(c: Candidate) { return !!c.value?.hypothesis || c.provenance === 'inferred' || c.confidence < 0.6 || !!c.value?.uncertain; }

function SourceRef({ c, sources }: { c: Candidate; sources: BSource[] }) {
  const s = sources.find((x) => x.id === srcOf(c));
  const l = c.location ?? {};
  const r = l.region;
  return (
    <div className="small muted stack" style={{ gap: 2 }}>
      <span>מקור: {s ? <>{s.kind === 'url' ? 'אתר' : s.kind === 'pdf' ? 'PDF' : 'תמונה'}{s.preferred && <> <span className="badge info">מועדף</span></>}{s.assetUrl && <> · <a href={s.assetUrl} target="_blank" rel="noopener noreferrer">פתיחת המקור</a></>}</> : <span className="mono">{srcOf(c) || '—'}</span>}</span>
      {(l.url || s?.url) && <span className="url-cell">{l.url ?? s?.url}</span>}
      {l.page != null && <span>עמוד {l.page}</span>}
      {r && <span className="ltr">אזור: x={Math.round(r.x ?? 0)} y={Math.round(r.y ?? 0)} {Math.round(r.width ?? 0)}×{Math.round(r.height ?? 0)}</span>}
      {l.selector && <span className="mono">{l.selector}</span>}
    </div>
  );
}

function Meta({ c }: { c: Candidate }) {
  return (
    <div className="row" style={{ gap: 4 }}>
      <span className={`badge ${c.provenance === 'extracted' ? 'ok' : 'warn'}`}>{c.provenance === 'extracted' ? 'חולץ מהמקור' : 'הוסק'}</span>
      <span className={`badge ${c.confidence >= 0.8 ? 'ok' : c.confidence >= 0.5 ? 'warn' : 'err'}`}>ביטחון {Math.round(c.confidence * 100)}%</span>
      <span className={`badge ${c.status === 'approved' ? 'ok' : c.status === 'rejected' ? 'err' : ''}`}>{c.status === 'approved' ? 'מאושר' : c.status === 'rejected' ? 'בוטל' : 'ממתין'}</span>
    </div>
  );
}

export function useCandidateActions(onUpdate: (c: Candidate) => void) {
  const act = useAction();
  return {
    busy: act.busy, error: act.error,
    approve: async (c: Candidate, value?: ValuePatch) => { const r = await act.run(() => api.post<Candidate>(`/api/brand-candidates/${c.id}/approve`, value !== undefined ? { value } : {})); if (r) onUpdate({ ...c, ...r }); },
    patch: async (c: Candidate, body: { status?: 'approved' | 'rejected' | 'pending'; value?: ValuePatch }, ok?: string) => { const r = await act.run(() => api.patch<Candidate>(`/api/brand-candidates/${c.id}`, body), ok); if (r) onUpdate({ ...c, ...r }); },
  };
}

export function LogoCandidates({ items, sources, actions }: { items: Candidate[]; sources: BSource[]; actions: ReturnType<typeof useCandidateActions> }) {
  if (!items.length) return <div className="alert warn small">לא נמצא לוגו במקורות. העלו את קובץ הלוגו המקורי — המערכת אינה משחזרת לוגו באמצעות AI.</div>;
  return (
    <div className="stack">
      {items.map((c) => (
        <article key={c.id} className={`cand ${c.status}`} aria-label={`מועמד לוגו ${c.id}`}>
          <div className="img">{c.assetUrl ? <img src={c.assetUrl} alt="מועמד לוגו" /> : <span className="muted small">אין תצוגה</span>}</div>
          <div className="stack" style={{ gap: 6 }}>
            <Meta c={c} />
            <SourceRef c={c} sources={sources} />
            <LogoActions c={c} actions={actions} />
          </div>
        </article>
      ))}
    </div>
  );
}

function LogoActions({ c, actions }: { c: Candidate; actions: ReturnType<typeof useCandidateActions> }) {
  const v = c.value ?? {};
  const reg = c.location?.region;
  const sc: Crop | null = v.suggestedCrop ?? (reg ? { left: reg.x ?? 0, top: reg.y ?? 0, width: reg.width ?? 0, height: reg.height ?? 0 } : null);
  const [crop, setCrop] = useState<Crop>(sc ?? { left: 0, top: 0, width: 100, height: 100 });
  const [showCrop, setShowCrop] = useState(false);
  const blocked = !c.asset_id || (v.needsCropApproval && !v.processed);
  return (
    <>
      {v.message && <div className="alert warn small">{v.message}</div>}
      {v.needsCropApproval && !v.processed && <span className="badge warn">דורש אישור חיתוך מסומן באיכות המקור</span>}
      {v.processed && <span className="badge info">עובד: {[v.processed.crop && 'חיתוך', v.processed.removeBg && 'הסרת רקע'].filter(Boolean).join(' + ')}</span>}
      <div className="row">
        <label className="small" htmlFor={`lv-${c.id}`}>גרסת לוגו</label>
        <select id={`lv-${c.id}`} value={v.variant ?? ''} disabled={actions.busy} onChange={(e) => e.target.value && actions.patch(c, { value: { variant: e.target.value } }, 'עודכן')}>
          <option value="">—</option>{Object.entries(LOGO_VARIANT_L).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      {showCrop && (
        <fieldset className="box">
          <legend>חיתוך (פיקסלים במקור)</legend>
          <div className="row">
            {(['left', 'top', 'width', 'height'] as const).map((k) => (
              <label key={k} className="field" style={{ width: 90 }}><span>{{ left: 'משמאל', top: 'מלמעלה', width: 'רוחב', height: 'גובה' }[k]}</span>
                <input type="number" className="ltr" min={k === 'width' || k === 'height' ? 4 : 0} value={crop[k]} onChange={(e) => setCrop({ ...crop, [k]: Number(e.target.value) })} /></label>
            ))}
          </div>
          <div className="small muted">החיתוך נשמר כנכס חדש באיכות המקור; המקור המקורי נשמר.</div>
          <div className="row" style={{ marginTop: 6 }}>
            <button type="button" className="btn small primary" disabled={actions.busy} onClick={async () => { await actions.patch(c, { value: { crop } }, 'החיתוך אושר'); setShowCrop(false); }}>אישור חיתוך</button>
            <button type="button" className="btn small" onClick={() => setShowCrop(false)}>ביטול</button>
          </div>
        </fieldset>
      )}
      <div className="row">
        <button className="btn small primary" disabled={actions.busy || c.status === 'approved' || blocked} title={blocked ? 'יש לאשר חיתוך או להעלות קובץ לוגו' : undefined} onClick={() => actions.approve(c)}>בחירה / אישור</button>
        {c.asset_id && <button className="btn small" disabled={actions.busy} onClick={() => setShowCrop(true)} aria-expanded={showCrop}>חיתוך…</button>}
        {c.asset_id && <button className="btn small" disabled={actions.busy} onClick={() => actions.patch(c, { value: { removeBg: true } }, 'הרקע הוסר — נוצר נכס חדש')}>הסרת רקע</button>}
        <button className="btn small danger" disabled={actions.busy || c.status === 'rejected'} onClick={() => actions.patch(c, { status: 'rejected' }, 'המועמד בוטל')}>ביטול</button>
      </div>
      {blocked && <div className="small muted">אין מקור נקי — אשרו חיתוך מסומן או העלו את קובץ הלוגו המקורי ללקוח. לוגו אינו משוחזר ב-AI.</div>}
    </>
  );
}

export function ColorCandidates({ items, sources, actions }: { items: Candidate[]; sources: BSource[]; actions: ReturnType<typeof useCandidateActions> }) {
  const brand = items.filter((c) => !isPhotoColor(c));
  const photo = items.filter(isPhotoColor);
  return (
    <div className="stack">
      <h3 style={{ margin: 0 }}>צבעי מותג</h3>
      {brand.length ? <div className="palette">{brand.map((c) => <ColorCard key={c.id} c={c} sources={sources} actions={actions} />)}</div> : <div className="small muted">לא זוהו צבעי מותג.</div>}
      <h3 style={{ margin: '8px 0 0' }}>צבעים דומיננטיים בצילומים</h3>
      <div className="small muted">צבע דומיננטי בצילום אינו בהכרח צבע המותג. הם מוצגים בנפרד ואינם נכללים אלא אם מאשרים במפורש.</div>
      {photo.length ? <div className="palette">{photo.map((c) => <ColorCard key={c.id} c={c} sources={sources} actions={actions} photo />)}</div> : <div className="small muted">אין.</div>}
    </div>
  );
}

function ColorCard({ c, sources, actions, photo }: { c: Candidate; sources: BSource[]; actions: ReturnType<typeof useCandidateActions>; photo?: boolean }) {
  const v = c.value ?? {};
  const initRole: string = [v.role, v.suggestedRole].find((r) => r && r in COLOR_ROLES) ?? 'other';
  const [hex, setHex] = useState<string>(v.hex ?? '#000000');
  const [role, setRole] = useState<string>(initRole);
  const bad = !HEX_RE.test(hex);
  const dirty = hex.toLowerCase() !== String(v.hex ?? '').toLowerCase() || role !== initRole;
  return (
    <div className="pc" style={c.status === 'rejected' ? { opacity: 0.55 } : photo ? { borderStyle: 'dashed' } : undefined}>
      <div className="big" style={{ background: HEX_RE.test(hex) ? hex : '#fff' }} aria-hidden="true" />
      <div className="row" style={{ gap: 4 }}>
        <input type="color" aria-label="בורר צבע" value={HEX_RE.test(hex) ? hex.toLowerCase() : '#000000'} onChange={(e) => setHex(e.target.value)} />
        <input className="mono" aria-label="HEX" value={hex} onChange={(e) => setHex(e.target.value.trim())} style={{ width: 88, borderColor: bad ? 'var(--danger)' : undefined }} aria-invalid={bad} />
      </div>
      <label className="sr-only" htmlFor={`role-${c.id}`}>תפקיד</label>
      <select id={`role-${c.id}`} value={role} onChange={(e) => setRole(e.target.value)}>{Object.entries(COLOR_ROLES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      {v.share != null && <span className="small muted">נוכחות: {Math.round(v.share * 100)}%</span>}
      {v.conflict && <span className="badge err">בסתירה</span>}
      <Meta c={c} />
      <SourceRef c={c} sources={sources} />
      <div className="row" style={{ gap: 4 }}>
        <button className="btn small primary" disabled={actions.busy || bad || (c.status === 'approved' && !dirty)} onClick={() => actions.approve(c, { hex: hex.toUpperCase(), role })}>{dirty ? 'שמירה ואישור' : 'אישור'}</button>
        <button className="btn small danger" disabled={actions.busy || c.status === 'rejected'} onClick={() => actions.patch(c, { status: 'rejected' })}>ביטול</button>
      </div>
    </div>
  );
}

export function FontCandidates({ items, sources, actions }: { items: Candidate[]; sources: BSource[]; actions: ReturnType<typeof useCandidateActions> }) {
  if (!items.length) return <div className="small muted">לא זוהה פונט.</div>;
  return (
    <div className="stack">
      <div className="alert info small">אין הטמעת פונט מרחוק. פונט ייכלל בבאנר רק עם קובץ ורישיון הטמעה שהועלו לספריית הלקוח. אישור כאן שומר את שם הפונט בלבד.</div>
      {items.map((c) => {
        const v = c.value ?? {};
        return (
          <div key={c.id} className="rule-card stack" style={{ gap: 6, opacity: c.status === 'rejected' ? 0.55 : 1 }}>
            <div className="row">
              <strong className="ltr" style={{ fontSize: 16 }}>{v.family ?? v.name ?? '—'}</strong>
              {isUncertainFont(c) && <span className="badge warn">השערה</span>}
              {v.systemFont && <span className="badge">פונט מערכת</span>}
              {v.weights && <span className="mono small">{[].concat(v.weights).join(',')}</span>}
              {v.alternative && <span className="small muted">חלופה מוצעת: <span className="ltr">{v.alternative}</span></span>}
            </div>
            {v.reason && <div className="small" style={{ color: 'var(--warn)' }}>{v.reason}</div>}
            <Meta c={c} />
            <SourceRef c={c} sources={sources} />
            <div className="small" style={{ color: 'var(--warn)' }}>{v.note ?? 'אין קובץ פונט — יישמר כשם בלבד ולא יוטמע.'}</div>
            <div className="row">
              <button className="btn small primary" disabled={actions.busy || c.status === 'approved'} onClick={() => actions.approve(c)}>אישור</button>
              <button className="btn small danger" disabled={actions.busy || c.status === 'rejected'} onClick={() => actions.patch(c, { status: 'rejected' })}>ביטול</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function OtherCandidates({ items, sources, actions }: { items: Candidate[]; sources: BSource[]; actions: ReturnType<typeof useCandidateActions> }) {
  if (!items.length) return <div className="small muted">אין.</div>;
  return (
    <div className="stack">
      {items.map((c) => (
        <div key={c.id} className="rule-card stack" style={{ gap: 6, opacity: c.status === 'rejected' ? 0.55 : 1 }}>
          <div className="row"><span className="badge">{KIND_L[c.kind] ?? c.kind}</span>{c.assetUrl && <img className="thumb" src={c.assetUrl} alt="" />}<span className="grow">{describe(c.value)}</span></div>
          <Meta c={c} />
          <SourceRef c={c} sources={sources} />
          <div className="row">
            <button className="btn small primary" disabled={actions.busy || c.status === 'approved'} onClick={() => actions.approve(c)}>אישור</button>
            <button className="btn small danger" disabled={actions.busy || c.status === 'rejected'} onClick={() => actions.patch(c, { status: 'rejected' })}>ביטול</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function describe(v: any): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.text ?? v.description ?? v.name ?? v.label ?? JSON.stringify(v);
  return String(v);
}

export function ConflictList({ conflicts, candidates, actions }: { conflicts: any[]; candidates: Candidate[]; actions: ReturnType<typeof useCandidateActions> }) {
  if (!conflicts.length) return <div className="small muted">לא נמצאו סתירות בין המקורות.</div>;
  const choose = async (c: any, candidateId: string) => {
    const chosen = candidates.find((x) => x.id === candidateId);
    if (chosen) await actions.approve(chosen);
    for (const other of (c.candidateIds ?? []) as string[]) {
      const o = candidates.find((x) => x.id === other);
      if (o && other !== candidateId && o.status !== 'rejected') await actions.patch(o, { status: 'rejected' });
    }
  };
  return (
    <ul className="list-plain">
      {conflicts.map((c, i) => (
        <li key={i}>
          <strong>{KIND_L[c.kind] ?? c.kind ?? 'סתירה'}</strong>{c.role && <span className="small muted"> · {c.role}</span>}
          {c.message && <div className="small">{c.message}</div>}
          {Array.isArray(c.values) && (
            <div className="conflict-sides" style={{ marginTop: 4 }}>
              {c.values.map((x: any, j: number) => {
                const val = typeof x?.value === 'object' ? (x.value?.hex ?? describe(x.value)) : String(x?.value ?? '');
                const cand = candidates.find((y) => y.id === x?.candidateId);
                return (
                  <div className="conflict-side" key={j}>
                    {/^#[0-9a-f]{6}$/i.test(val) ? <span className="row"><span className="swatch" style={{ background: val }} /><span className="mono">{val}</span></span> : <span className="ltr">{val}</span>}
                    {x?.fromPreferred && <span className="badge info">מהמקור המועדף</span>}
                    {cand && <span className="small muted">סטטוס: {cand.status === 'approved' ? 'מאושר' : cand.status === 'rejected' ? 'בוטל' : 'ממתין'}</span>}
                    {x?.candidateId && <button className="btn small primary" disabled={actions.busy || cand?.status === 'approved'} onClick={() => choose(c, x.candidateId)}>בחירת ערך זה</button>}
                  </div>
                );
              })}
            </div>
          )}
          {!Array.isArray(c.values) && !c.message && <pre className="json-val">{JSON.stringify(c)}</pre>}
        </li>
      ))}
    </ul>
  );
}
